"use client"

import * as React from "react"
import {
  History,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  User,
  Clock,
  Layers,
  FileText,
  X,
  Copy,
  Check,
  Plus,
  Pencil,
  Trash2,
  Building2,
  FolderOpen
} from "lucide-react"
import { TabsContent } from "@/components/ui/tabs"
import { Button } from "@/components/ui/button"
import { Account, HistoryRecord, HistoryResponse } from "@/types/workspace"
import { toast } from "sonner"

export interface HistorySettingsTabProps {
  currentAccount: Account | null
  profileRole?: string
  t?: Record<string, string>
}

// Mapeamento amigável de nomes de entidades
const ENTITY_LABELS: Record<string, { label: string; icon: any }> = {
  workspaces: { label: "Workspace", icon: FolderOpen },
  accounts: { label: "Empresa", icon: Building2 },
  profiles: { label: "Membro / Perfil", icon: User },
  installed_apps: { label: "Aplicativo", icon: Layers },
  installed_widgets: { label: "Widget", icon: Layers },
  workspace_members: { label: "Acesso a Workspace", icon: User },
  apps: { label: "App Marketplace", icon: Layers },
}

export function HistorySettingsTab({
  currentAccount,
  profileRole,
  t,
}: HistorySettingsTabProps) {
  const [records, setRecords] = React.useState<HistoryRecord[]>([])
  const [totalCount, setTotalCount] = React.useState(0)
  const [page, setPage] = React.useState(1)
  const [limit, setLimit] = React.useState(15)
  const [totalPages, setTotalPages] = React.useState(1)
  const [isLoading, setIsLoading] = React.useState(false)

  // Filtros
  const [selectedAction, setSelectedAction] = React.useState("ALL")
  const [selectedEntity, setSelectedEntity] = React.useState("ALL")
  const [selectedWorkspace, setSelectedWorkspace] = React.useState("ALL")

  // Modal de Detalhes
  const [selectedRecord, setSelectedRecord] = React.useState<HistoryRecord | null>(null)
  const [copiedField, setCopiedField] = React.useState<string | null>(null)

  const fetchHistory = React.useCallback(async (targetPage = page) => {
    if (!currentAccount?.id) return
    setIsLoading(true)

    try {
      const res = await fetch("/api/graphql", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          query: `
            query GetAccountHistory(
              $accountId: ID!
              $workspaceId: ID
              $action: String
              $entityType: String
              $page: Int
              $limit: Int
            ) {
              accountHistory(
                accountId: $accountId
                workspaceId: $workspaceId
                action: $action
                entityType: $entityType
                page: $page
                limit: $limit
              ) {
                items {
                  id
                  accountId
                  workspaceId
                  entityType
                  entityId
                  action
                  performedBy
                  changes
                  oldData
                  newData
                  ipAddress
                  userAgent
                  createdAt
                  user {
                    id
                    name
                    email
                    avatarUrl
                  }
                  workspace {
                    id
                    name
                    slug
                  }
                }
                totalCount
                page
                limit
                totalPages
                hasMore
              }
            }
          `,
          variables: {
            accountId: currentAccount.id,
            workspaceId: selectedWorkspace === "ALL" ? null : selectedWorkspace,
            action: selectedAction === "ALL" ? null : selectedAction,
            entityType: selectedEntity === "ALL" ? null : selectedEntity,
            page: targetPage,
            limit: limit,
          },
        }),
      })

      const json = await res.json()
      if (json.errors && json.errors.length > 0) {
        console.error("Erro ao carregar histórico:", json.errors)
        toast.error(json.errors[0]?.message || "Erro ao carregar histórico.")
        return
      }

      const data: HistoryResponse = json.data?.accountHistory
      if (data) {
        setRecords(data.items || [])
        setTotalCount(data.totalCount || 0)
        setPage(data.page || 1)
        setTotalPages(data.totalPages || 1)
      }
    } catch (err) {
      console.error("Erro na requisição de histórico:", err)
      toast.error("Não foi possível carregar os registros.")
    } finally {
      setIsLoading(false)
    }
  }, [currentAccount?.id, selectedWorkspace, selectedAction, selectedEntity, page, limit])

  // Recarregar quando filtros mudarem
  React.useEffect(() => {
    setPage(1)
    fetchHistory(1)
  }, [selectedAction, selectedEntity, selectedWorkspace, limit, currentAccount?.id])

  const handleCopy = (text: string, fieldName: string) => {
    navigator.clipboard.writeText(text)
    setCopiedField(fieldName)
    toast.success("Copiado para a área de transferência!")
    setTimeout(() => setCopiedField(null), 2000)
  }

  const parseJsonSafe = (val?: string | null) => {
    if (!val) return null
    try {
      return typeof val === "string" ? JSON.parse(val) : val
    } catch {
      return val
    }
  }

  const formatDateTime = (iso: string) => {
    try {
      const d = new Date(iso)
      return new Intl.DateTimeFormat("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      }).format(d)
    } catch {
      return iso
    }
  }

  return (
    <TabsContent value="history" className="outline-none space-y-6">
      {/* Cabeçalho da Aba */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h3 className="text-lg font-bold text-zinc-900 dark:text-white flex items-center gap-2 font-sans">
            <History className="size-5 text-[#004EB1]" />
            Registros de Auditoria & Histórico
          </h3>
          <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1 font-sans">
            Linha do tempo detalhada de todas as alterações, criações e exclusões registradas no banco de dados da empresa.
          </p>
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={() => fetchHistory(page)}
          disabled={isLoading}
          className="h-9 px-3 text-xs flex items-center gap-2 cursor-pointer shrink-0"
        >
          <RefreshCw className={`size-3.5 ${isLoading ? "animate-spin text-[#004EB1]" : ""}`} />
          <span>Atualizar</span>
        </Button>
      </div>

      {/* Barra de Filtros */}
      <div className="border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 rounded-2xl p-4 shadow-sm font-sans">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {/* Filtro por Ação */}
          <div className="relative">
            <select
              value={selectedAction}
              onChange={(e) => setSelectedAction(e.target.value)}
              className="w-full h-9 text-xs bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg px-3 focus:outline-none focus:border-[#004EB1] cursor-pointer text-zinc-700 dark:text-zinc-300"
            >
              <option value="ALL">Todas as Ações</option>
              <option value="INSERT">Inserção</option>
              <option value="UPDATE">Atualização</option>
              <option value="DELETE">Exclusão</option>
            </select>
          </div>

          {/* Filtro por Entidade */}
          <div className="relative">
            <select
              value={selectedEntity}
              onChange={(e) => setSelectedEntity(e.target.value)}
              className="w-full h-9 text-xs bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg px-3 focus:outline-none focus:border-[#004EB1] cursor-pointer text-zinc-700 dark:text-zinc-300"
            >
              <option value="ALL">Todas as Tabelas / Tipos</option>
              <option value="workspaces">Workspaces</option>
              <option value="accounts">Empresas (Accounts)</option>
              <option value="profiles">Perfis & Membros</option>
              <option value="installed_apps">Aplicativos Instalados</option>
              <option value="installed_widgets">Widgets Instalados</option>
              <option value="workspace_members">Membros de Workspace</option>
            </select>
          </div>

          {/* Filtro por Workspace */}
          <div className="relative">
            <select
              value={selectedWorkspace}
              onChange={(e) => setSelectedWorkspace(e.target.value)}
              className="w-full h-9 text-xs bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg px-3 focus:outline-none focus:border-[#004EB1] cursor-pointer text-zinc-700 dark:text-zinc-300"
            >
              <option value="ALL">Todos os Workspaces</option>
              {(currentAccount?.workspaces || []).map((ws) => (
                <option key={ws.id} value={ws.id}>
                  {ws.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Tabela de Registros */}
      <div className="border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 rounded-2xl overflow-hidden shadow-sm font-sans">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-zinc-50 dark:bg-zinc-850/60 border-b border-zinc-200 dark:border-zinc-800 text-zinc-500 dark:text-zinc-400 font-bold uppercase text-[10px] tracking-wider">
              <tr>
                <th className="py-3 px-4">Ação</th>
                <th className="py-3 px-4">Objeto / Tabela</th>
                <th className="py-3 px-4">ID do Objeto</th>
                <th className="py-3 px-4">Usuário</th>
                <th className="py-3 px-4">Workspace</th>
                <th className="py-3 px-4">Data & Horário</th>
                <th className="py-3 px-4 text-right">Detalhes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-150 dark:divide-zinc-800">
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-zinc-400">
                    <RefreshCw className="size-6 animate-spin mx-auto mb-2 text-[#004EB1]" />
                    <span>Carregando registros de auditoria...</span>
                  </td>
                </tr>
              ) : records.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-zinc-400">
                    <History className="size-8 mx-auto mb-2 text-zinc-300 dark:text-zinc-700" />
                    <p className="font-semibold text-zinc-600 dark:text-zinc-300">Nenhum registro encontrado</p>
                    <p className="text-[11px] text-zinc-400 mt-0.5">
                      Tente alterar os filtros para visualizar outros eventos.
                    </p>
                  </td>
                </tr>
              ) : (
                records.map((rec) => {
                  const entityInfo = ENTITY_LABELS[rec.entityType] || {
                    label: rec.entityType,
                    icon: Layers,
                  }
                  const EntityIcon = entityInfo.icon

                  return (
                    <tr
                      key={rec.id}
                      onClick={() => setSelectedRecord(rec)}
                      className="hover:bg-zinc-50/80 dark:hover:bg-zinc-850/40 transition-colors cursor-pointer group"
                    >
                      {/* Ação */}
                      <td className="py-3.5 px-4 whitespace-nowrap">
                        {rec.action === "INSERT" ? (
                          <span className="inline-flex items-center gap-1 p-2 rounded-full text-[10px] font-extrabold bg-emerald-500/10 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-400" title="Inserção">
                            <Plus className="size-2.5" />
                          </span>
                        ) : rec.action === "UPDATE" ? (
                          <span className="inline-flex items-center gap-1 p-2 rounded-full text-[10px] font-extrabold bg-blue-500/10 text-[#004EB1] dark:bg-blue-950/40 dark:text-blue-400" title="Atualização">
                            <Pencil className="size-2.5" />
                          </span>
                        ) : rec.action === "DELETE" ? (
                          <span className="inline-flex items-center gap-1 p-2 rounded-full text-[10px] font-extrabold bg-red-500/10 text-red-600 dark:bg-red-950/40 dark:text-red-400" title="Exclusão">
                            <Trash2 className="size-2.5" />
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 p-2 rounded-full text-[10px] font-extrabold bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400 uppercase" title={rec.action}>
                            {rec.action}
                          </span>
                        )}
                      </td>

                      {/* Objeto / Tabela */}
                      <td className="py-3.5 px-4 whitespace-nowrap">
                        <div className="flex items-center gap-1.5 font-bold text-zinc-800 dark:text-zinc-200">
                          <EntityIcon className="size-3.5 text-zinc-400" />
                          <span>{entityInfo.label}</span>
                        </div>
                      </td>

                      {/* ID do Objeto */}
                      <td className="py-3.5 px-4 whitespace-nowrap font-mono text-[11px] text-zinc-500 max-w-[140px] truncate" title={rec.entityId}>
                        {rec.entityId}
                      </td>

                      {/* Usuário Responsável */}
                      <td className="py-3.5 px-4 whitespace-nowrap">
                        {rec.user ? (
                          <div className="flex items-center gap-2">
                            <div className="size-5 rounded-full bg-zinc-200 dark:bg-zinc-700 flex items-center justify-center font-bold text-[9px] text-zinc-700 dark:text-zinc-200 overflow-hidden shrink-0">
                              {rec.user.avatarUrl ? (
                                <img src={rec.user.avatarUrl} alt="" className="size-full object-cover" />
                              ) : (
                                (rec.user.name || "U")[0].toUpperCase()
                              )}
                            </div>
                            <span className="font-semibold text-zinc-800 dark:text-zinc-200 truncate max-w-[120px]">
                              {rec.user.name}
                            </span>
                          </div>
                        ) : (
                          <span className="text-zinc-400 italic text-[11px]">Sistema</span>
                        )}
                      </td>

                      {/* Workspace */}
                      <td className="py-3.5 px-4 whitespace-nowrap">
                        {rec.workspace ? (
                          <span className="bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300 px-2 py-0.5 rounded font-medium text-[11px]">
                            {rec.workspace.name}
                          </span>
                        ) : (
                          <span className="text-zinc-400 text-[11px]">—</span>
                        )}
                      </td>

                      {/* Data & Horário */}
                      <td className="py-3.5 px-4 whitespace-nowrap text-zinc-500 font-mono text-[11px]">
                        {formatDateTime(rec.createdAt)}
                      </td>

                      {/* Botão Detalhes */}
                      <td className="py-3.5 px-4 text-right whitespace-nowrap">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation()
                            setSelectedRecord(rec)
                          }}
                          className="px-2.5 py-1 rounded-lg border border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300 hover:text-[#004EB1] hover:border-[#004EB1] dark:hover:text-blue-400 text-[11px] font-bold transition-colors cursor-pointer"
                        >
                          Ver
                        </button>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Rodapé com Paginação Otimizada */}
        <div className="p-4 border-t border-zinc-200 dark:border-zinc-800 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs select-none">
          <div className="flex items-center gap-3 text-zinc-500">
            <span>
              Total de <strong>{totalCount}</strong> registro{totalCount === 1 ? "" : "s"}
            </span>

            <div className="flex items-center gap-1.5 ml-2 border-l border-zinc-200 dark:border-zinc-800 pl-3">
              <span className="text-[11px]">Por página:</span>
              <select
                value={limit}
                onChange={(e) => setLimit(Number(e.target.value))}
                className="bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded px-1.5 py-0.5 text-xs focus:outline-none cursor-pointer"
              >
                <option value={10}>10</option>
                <option value={15}>15</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-zinc-500 font-medium">
              Página <strong>{page}</strong> de <strong>{totalPages}</strong>
            </span>

            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const newPage = Math.max(1, page - 1)
                  setPage(newPage)
                  fetchHistory(newPage)
                }}
                disabled={page <= 1 || isLoading}
                className="h-8 w-8 p-0 cursor-pointer"
                title="Página Anterior"
              >
                <ChevronLeft className="size-4" />
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const newPage = Math.min(totalPages, page + 1)
                  setPage(newPage)
                  fetchHistory(newPage)
                }}
                disabled={page >= totalPages || isLoading}
                className="h-8 w-8 p-0 cursor-pointer"
                title="Próxima Página"
              >
                <ChevronRight className="size-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* MODAL DE DETALHES DO REGISTRO DE AUDITORIA */}
      {selectedRecord && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-in fade-in duration-200 font-sans">
          <div className="w-full max-w-2xl bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[88vh]">
            {/* Header do Modal */}
            <div className="p-4 px-6 border-b border-zinc-200 dark:border-zinc-800 flex items-center justify-between bg-zinc-50/50 dark:bg-zinc-850/50">
              <div className="flex items-center gap-3">
                <div className="size-8 rounded-lg bg-[#004EB1]/10 text-[#004EB1] dark:bg-blue-950/40 dark:text-blue-400 flex items-center justify-center">
                  <FileText className="size-4" />
                </div>
                <div>
                  <h4 className="text-sm font-bold text-zinc-900 dark:text-white flex items-center gap-2">
                    <span>Detalhes do Registro</span>
                    <span className="text-xs px-2 py-0.5 rounded font-mono font-normal bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300">
                      {selectedRecord.id}
                    </span>
                  </h4>
                  <p className="text-[11px] text-zinc-400">
                    Ocorrido em {formatDateTime(selectedRecord.createdAt)}
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setSelectedRecord(null)}
                className="size-8 rounded-lg flex items-center justify-center text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors cursor-pointer"
              >
                <X className="size-4" />
              </button>
            </div>

            {/* Conteúdo do Modal */}
            <div className="p-6 overflow-y-auto space-y-5 text-xs">
              {/* Grid de Metadados Principais (sem ID do Objeto) */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 bg-zinc-50 dark:bg-zinc-950 p-3.5 rounded-xl border border-zinc-200 dark:border-zinc-800">
                <div>
                  <span className="text-[10px] font-bold text-zinc-400 uppercase block">Ação</span>
                  <span className="font-bold text-zinc-900 dark:text-white">
                    {selectedRecord.action}
                  </span>
                </div>

                <div>
                  <span className="text-[10px] font-bold text-zinc-400 uppercase block">Tabela / Objeto</span>
                  <span className="font-bold text-zinc-900 dark:text-white">
                    {selectedRecord.entityType}
                  </span>
                </div>

                <div>
                  <span className="text-[10px] font-bold text-zinc-400 uppercase block">Autor</span>
                  <span className="font-semibold text-zinc-900 dark:text-white">
                    {selectedRecord.user?.name || selectedRecord.performedBy || "Sistema"}
                  </span>
                </div>

                <div>
                  <span className="text-[10px] font-bold text-zinc-400 uppercase block">Workspace</span>
                  <span className="font-semibold text-zinc-900 dark:text-white">
                    {selectedRecord.workspace?.name || "Global / Conta"}
                  </span>
                </div>

                {selectedRecord.ipAddress && (
                  <div className="col-span-2">
                    <span className="text-[10px] font-bold text-zinc-400 uppercase block">IP de Origem</span>
                    <span className="font-mono text-zinc-600 dark:text-zinc-300">
                      {selectedRecord.ipAddress}
                    </span>
                  </div>
                )}
              </div>

              {/* JSON Completo do Snapshot */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h5 className="font-bold text-xs text-zinc-800 dark:text-zinc-200 uppercase tracking-wider">
                    Snapshot dos Dados ({selectedRecord.action === "DELETE" ? "Antes da Exclusão" : "Após a Operação"})
                  </h5>
                  <button
                    type="button"
                    onClick={() => handleCopy(selectedRecord.newData || selectedRecord.oldData || "{}", "fullJson")}
                    className="flex items-center gap-1 text-[11px] text-[#004EB1] dark:text-blue-400 font-bold hover:underline cursor-pointer"
                  >
                    {copiedField === "fullJson" ? <Check className="size-3 text-emerald-500" /> : <Copy className="size-3" />}
                    <span>Copiar JSON</span>
                  </button>
                </div>

                <pre className="p-3.5 rounded-xl bg-zinc-950 text-zinc-200 font-mono text-[11px] overflow-x-auto max-h-72 leading-relaxed border border-zinc-800">
                  {JSON.stringify(
                    parseJsonSafe(selectedRecord.newData) || parseJsonSafe(selectedRecord.oldData) || {},
                    null,
                    2
                  )}
                </pre>
              </div>
            </div>

            {/* Footer do Modal */}
            <div className="p-4 border-t border-zinc-200 dark:border-zinc-800 flex justify-end bg-zinc-50 dark:bg-zinc-850/50">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedRecord(null)}
                className="cursor-pointer"
              >
                Fechar
              </Button>
            </div>
          </div>
        </div>
      )}
    </TabsContent>
  )
}
